import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { LuClipboardPaste } from "react-icons/lu";
import { appendAdderNodeToPipeline, editAdderNode } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './AdderConfigModal.css';

// Set the app element for accessibility
Modal.setAppElement('#root');

const UNSPECIFIED_INPUT_A = 'Column A not specified';
const UNSPECIFIED_INPUT_B = 'Column B not specified';
const UNSPECIFIED_SUM = 'Sum column not specified';

const normalizeOptionalColumn = (value, sentinelLabel = null) => {
  if (value === undefined || value === null) {
    return '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return '';
  }
  if (sentinelLabel && trimmed.toLowerCase() === sentinelLabel.toLowerCase()) {
    return '';
  }
  return String(value);
};

const normalizeRequiredSumColumn = (value) => normalizeOptionalColumn(value, UNSPECIFIED_SUM);

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  }
};

const AdderConfigModal = ({ isOpen, onClose, onConfigured, initialData = {}, isReconfiguration = false }) => {
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [inputA_col_name, setInputA_col_name] = useState(
    normalizeOptionalColumn(initialData.inputA_col_name, UNSPECIFIED_INPUT_A)
  );
  const [inputB_col_name, setInputB_col_name] = useState(
    normalizeOptionalColumn(initialData.inputB_col_name, UNSPECIFIED_INPUT_B)
  );
  const [sum_column_name, setSum_column_name] = useState(normalizeRequiredSumColumn(initialData.sum_column_name));
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Determine if we're in edit mode (has a name AND isConfigured is true but not reconfiguring)
  const isEditing = !!initialData.name && initialData.isConfigured === true && !isReconfiguration;
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  // Keep local form state in sync when opening modal for edits/re-edits.
  useEffect(() => {
    if (!isOpen) return;
    setNodeName(initialData.name || '');
    setInputA_col_name(normalizeOptionalColumn(initialData.inputA_col_name, UNSPECIFIED_INPUT_A));
    setInputB_col_name(normalizeOptionalColumn(initialData.inputB_col_name, UNSPECIFIED_INPUT_B));
    setSum_column_name(normalizeRequiredSumColumn(initialData.sum_column_name));
    setSubmitError(null);
  }, [isOpen, initialData.name, initialData.inputA_col_name, initialData.inputB_col_name, initialData.sum_column_name]);
  
  // Function to paste from clipboard for input A column
  const pasteFromClipboardA = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setInputA_col_name(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  // Function to paste from clipboard for input B column
  const pasteFromClipboardB = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setInputB_col_name(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  // Function to paste from clipboard for sum column
  const pasteFromClipboardSum = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setSum_column_name(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }

      const normalizedInputA = normalizeOptionalColumn(inputA_col_name, UNSPECIFIED_INPUT_A);
      const normalizedInputB = normalizeOptionalColumn(inputB_col_name, UNSPECIFIED_INPUT_B);
      const normalizedSum = normalizeRequiredSumColumn(sum_column_name);

      // Make sure we have valid column names
      if (!normalizedSum) {
        setSubmitError({ title: 'Missing sum column', message: 'Please enter a valid name for the Sum Column.' });
        return;
      }
      
      // Get the input node names and position from initialData
      const inputNodes = initialData.inputNodes || {};
      const position = initialData.position || { x: 0, y: 0 };
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      // Log the configuration mode
      console.log("Configuration mode:", {
        isEditing,
        isReconfiguration,
        name: initialData.name,
        isConfigured: initialData.isConfigured
      });
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          if (isEditing) {
            // We're editing an existing node
            console.log(`Calling API to edit adder node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New input A column name: ${normalizedInputA}, New input B column name: ${normalizedInputB}, New sum column name: ${normalizedSum}`);
            
            // In edit mode, we only update the column names
            await editAdderNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              normalizedInputA || null,
              normalizedInputB || null,
              normalizedSum
            );
            
            console.log(`Successfully edited adder node ${initialData.name}`);
          } else if (!isReconfiguration && inputNodes.input1 && inputNodes.input2) {
            // We're creating a new node
            console.log(`Calling API to append adder node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Input node 1: ${inputNodes.input1}, Input node 2: ${inputNodes.input2}`);
            console.log(`Input A Column: ${normalizedInputA}, Input B Column: ${normalizedInputB}, Sum Column: ${normalizedSum}`);
            console.log(`Position: (${position.x}, ${position.y})`);
            
            await appendAdderNodeToPipeline(
              session.pipeline_name,
              nodeName,
              normalizedInputA || '',
              normalizedInputB || '',
              normalizedSum,
              inputNodes.input1,
              inputNodes.input2,
              position.x,
              position.y
            );
            
            console.log(`Successfully added adder node ${nodeName} to backend`);
          } else if (isReconfiguration) {
            console.log(`Skipping API call for node ${nodeName} as it's being reconfigured by parent component`);
          }
        } catch (apiError) {
          console.error(`Error calling ${isEditing ? 'editAdderNode' : 'appendAdderNodeToPipeline'}:`, apiError);
          const info = notifyError(apiError, {
            operation: isEditing ? OP.node.editAdder : OP.node.appendMerger,
            showToast: false,
          });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      } else {
        console.log(`Skipping API call for unsaved pipeline`);
      }
      
      // Call the callback to update the UI
      onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        inputA_col_name: normalizedInputA,
        inputB_col_name: normalizedInputB,
        sum_column_name: normalizedSum
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring adder node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Adder Configuration"
    >
      <div className="adder-modal">
        <button onClick={onClose} className="adder-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="adder-modal-title">
          {isReconfiguration ? "Reconfigure Adder Node" : 
           isEditing ? "Edit Adder Parameters" : 
           "Configure New Adder Node"}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="adder-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="adder-modal-field">
          <label className="adder-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="adder-modal-input"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`adder-modal-input ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="adder-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="adder-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>
        
        <div className="adder-modal-field">
          <label className="adder-modal-label">
            Input A Column Name:
          </label>
          <div className="input-with-icon">
            <input
              type="text"
              value={inputA_col_name}
              onChange={(e) => setInputA_col_name(e.target.value)}
              className="adder-modal-input"
              placeholder="Leave blank to auto-select last output column of first input"
              title="Optional: leave blank to auto-select the last output column from the first input node"
            />
            <button 
              type="button"
              onClick={pasteFromClipboardA}
              className="paste-button"
              title="Paste column name from clipboard"
            >
              <LuClipboardPaste size={18} />
            </button>
          </div>
        </div>

        <div className="adder-modal-field">
          <label className="adder-modal-label">
            Input B Column Name:
          </label>
          <div className="input-with-icon">
            <input
              type="text"
              value={inputB_col_name}
              onChange={(e) => setInputB_col_name(e.target.value)}
              className="adder-modal-input"
              placeholder="Leave blank to auto-select last output column of second input"
              title="Optional: leave blank to auto-select the last output column from the second input node"
            />
            <button 
              type="button"
              onClick={pasteFromClipboardB}
              className="paste-button"
              title="Paste column name from clipboard"
            >
              <LuClipboardPaste size={18} />
            </button>
          </div>
        </div>

        <div className="adder-modal-field">
          <label className="adder-modal-label">
            Sum Column Name:
          </label>
          <div className="input-with-icon">
            <input
              type="text"
              value={sum_column_name}
              onChange={(e) => setSum_column_name(e.target.value)}
              className="adder-modal-input"
              placeholder="Enter name for the sum column"
              title="Enter the name for the column that will contain the sum. Spaces and special characters are allowed."
            />
            <button 
              type="button"
              onClick={pasteFromClipboardSum}
              className="paste-button"
              title="Paste column name from clipboard"
            >
              <LuClipboardPaste size={18} />
            </button>
          </div>
        </div>
        
        <div className="adder-modal-button-container">
          <button 
            className="adder-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName)) || 
              !normalizeRequiredSumColumn(sum_column_name)
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : 
              !normalizeRequiredSumColumn(sum_column_name) ? 'Sum column name is required' : ''
            }
          >
            {isSubmitting ? 'Processing...' : 
              isReconfiguration ? 'Reconfigure' : 
              isEditing ? 'Save Changes' : 
              'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AdderConfigModal;